import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './NewEmployee.css';

function NewEmployee() {
  const navigate = useNavigate();
  const today = useMemo(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 10);
  }, []);

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    startingDate: today,
    roleId: '',
    taxNumber: '',
    insuranceNumber: '',
    salary: '',
    ratePerHour: '',
    isActive: true,
    notes: '',
  });

  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);
      setError('');

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/time-tracking/roles', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setRoles(response.data.roles || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load employee roles.');
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  const handleChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setStatus('');
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/time-tracking/employees', form, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setStatus('Employee saved successfully.');
      setTimeout(() => {
        navigate('/time-tracking/employees');
      }, 800);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        (err.response?.data?.errors && err.response.data.errors[0]?.msg) ||
        'Failed to save employee.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="new-employee-page">
      <div className="new-employee-side-handle">{'\u2039'}</div>

      <header className="new-employee-header">
        <button className="new-employee-back" onClick={() => navigate('/time-tracking/employees')}>{'\u2190'}</button>
        <div>
          <h1>New Employee</h1>
          <p>Employees can be selected when assigning work on a jobcard or when adding salary expenses in Accounting!</p>
        </div>
      </header>

      <form className="new-employee-card" onSubmit={handleSubmit}>
        <div className="employee-avatar-column">
          <div className="employee-avatar" aria-hidden="true">
            <div className="avatar-head" />
            <div className="avatar-body" />
          </div>
          <button className="avatar-edit" type="button" aria-label="Edit employee photo">
            {'\u270E'}
          </button>
        </div>

        <div className="employee-form-column">
          <div className="employee-form-row">
            <label>Employee's Full Name</label>
            <input
              value={form.fullName}
              onChange={handleChange('fullName')}
              placeholder="Enter Employee's name"
              disabled={saving}
              required
            />
          </div>

          <div className="employee-form-grid two-columns">
            <div className="employee-form-row">
              <label>Phone number*</label>
              <input
                value={form.phoneNumber}
                onChange={handleChange('phoneNumber')}
                placeholder="Enter Employee's Phone number"
                disabled={saving}
                required
              />
            </div>
            <div className="employee-form-row">
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                placeholder="Enter Employee's Email"
                disabled={saving}
              />
            </div>
          </div>

          <div className="employee-form-row">
            <label>Employee Starting Date*</label>
            <input
              type="date"
              value={form.startingDate}
              onChange={handleChange('startingDate')}
              disabled={saving}
              required
            />
          </div>

          <div className="employee-form-row">
            <label>Role*</label>
            <select
              value={form.roleId}
              onChange={handleChange('roleId')}
              disabled={saving || loadingRoles}
              required
            >
              <option value="">{loadingRoles ? 'Loading roles...' : ''}</option>
              {roles.map((role) => (
                <option key={role.RoleId} value={role.RoleId}>
                  {role.RoleName}
                </option>
              ))}
            </select>
          </div>

          <div className="employee-form-grid two-columns">
            <div className="employee-form-row">
              <label>Tax Number</label>
              <input
                value={form.taxNumber}
                onChange={handleChange('taxNumber')}
                placeholder="Enter Tax Number"
                disabled={saving}
              />
            </div>
            <div className="employee-form-row">
              <label>Insurance Number</label>
              <input
                value={form.insuranceNumber}
                onChange={handleChange('insuranceNumber')}
                placeholder="Enter Insurance Number"
                disabled={saving}
              />
            </div>
          </div>

          <div className="employee-form-grid two-columns">
            <div className="employee-form-row">
              <label>Salary (Tk)</label>
              <span className="field-hint">used in Reports-&gt;Employees</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.salary}
                onChange={handleChange('salary')}
                placeholder="enter employee salary"
                disabled={saving}
              />
            </div>
            <div className="employee-form-row">
              <label>Rate per Hour (Tk)</label>
              <span className="field-hint">used when creating a salary expense in Accounting</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.ratePerHour}
                onChange={handleChange('ratePerHour')}
                placeholder="enter rate per hour"
                disabled={saving}
              />
            </div>
          </div>

          <label className="employee-active-check">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={handleChange('isActive')}
              disabled={saving}
            />
            Active
          </label>

          <div className="employee-form-row">
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={handleChange('notes')}
              placeholder="Enter note"
              rows="5"
              disabled={saving}
            />
          </div>

          {error && <div className="employee-form-error">{error}</div>}
          {status && <div className="employee-form-success">{status}</div>}

          <div className="employee-form-actions">
            <button type="submit" className="employee-save-btn" disabled={saving || loadingRoles}>
              {saving ? 'Saving...' : 'Save Employee'}
            </button>
            <button
              type="button"
              className="employee-cancel-btn"
              onClick={() => navigate('/time-tracking/employees')}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default NewEmployee;
